import React, { ComponentType, useRef } from 'react';
import { Select as AntSelect, SelectProps as AntSelectProps } from 'antd';
import cx from 'classnames';
import styled from 'styled-components';
import { ArrowDownIcon } from 'assets/svg';
import classNames from 'classnames';

export const selectAll = 'selectAll';

const SelectContainer = styled.div`
  color: var(--grey-dark);
  width: 100%;
  position: relative;
  > label {
    font-weight: 300;
    color: var(--grey-dark);
  }
`;

interface SelectWrapperProps {
  height?: string;
}

const SelectWrapper = styled(AntSelect)<SelectWrapperProps>`
  width: 100%;
  .ant-select-selector {
    border-radius: 30px !important;
    min-height: ${(props) => props.height || '50px'};
    display: flex;
    align-items: center;
    box-shadow: none;
  }

  .ant-select-selector input {
    height: 48px !important;
    padding: 0 4px;
  }
  .ant-select-selection-placeholder {
    line-height: 48px;
    padding: 0 10px;
  }

  .ant-select-selection-item {
    padding-left: 10px;
    margin: 0;
    border: none;
    background-color: transparent;
  }

  .ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    background: #eaeaea;
  }

  &.ant-select-disabled {
    .ant-select-selection-item {
      color: var(--grey);
    }
  }

  .ant-select-arrow {
    top: 48% !important;
  }
`;

interface OptionAreaProps {
  activeColor?: string;
}

export const OptionArea = styled.div<OptionAreaProps>`
  .ant-select-item {
    color: var(--grey-dark);
    padding: 10px 20px 10px 20px;
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: none;
    color: ${({ activeColor }) => activeColor ?? 'var(--orange)'} !important;
    font-weight: normal;
  }

  .ant-select-item-option-disabled {
    background: #eaeaea;
  }
  .ant-select-item-option-state {
    margin-left: 0.5rem;
  }

  //scroll animation
  .ant-select-item.ant-select-item-option {
    &:hover {
      .ant-select-item-option-content.auto-scroll {
        animation: moveSlideshow 5s linear infinite;
      }
    }

    .ant-select-item-option-content {
      display: inline-block;
      overflow: unset;
      flex: none;
    }

    @keyframes moveSlideshow {
      100% {
        transform: translateX(-100%);
      }
    }
  }
`;

const LabelHeading = styled.label`
  font-size: 12px;
  line-height: 20px;
  font-weight: 500;
`;

export const addScrollAnimation = (event: any) => {
  try {
    const contentEl = event?.target?.querySelector('.ant-select-item-option-content');

    if (contentEl) {
      const wrapperContentEl = contentEl?.parentElement;
      const styles = window.getComputedStyle(wrapperContentEl);
      const width = wrapperContentEl.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);

      if (contentEl.offsetWidth > width) {
        contentEl.classList.add('auto-scroll');
      }
    }
  } catch (err) {
    console.error(err);
  }
};

export type SelectProps<VT = any> = AntSelectProps<VT, any> & {
  SelectAllOpt?: { label: string; id: string };
  wrapperClassName?: string;
  sortFunc?: <T>(options: T) => T;
  activeColor?: string;
  DropdownComponent?: ComponentType;
  label?: string;
  fontOption?: boolean;
};

const Select = ({
  label,
  options,
  SelectAllOpt,
  children,
  className,
  wrapperClassName,
  sortFunc,
  suffixIcon,
  activeColor,
  DropdownComponent,
  ...props
}: SelectProps) => {
  const ref = useRef(null);
  const sortedOptions = (sortFunc ? sortFunc(options) : options) ?? [];

  const DropdownComponentUsed = DropdownComponent ?? OptionArea;

  return (
    <SelectContainer className={classNames('momos-select', wrapperClassName)} ref={ref}>
      {!!label && <LabelHeading>{label}</LabelHeading>}
      <SelectWrapper
        className={cx(className)}
        showArrow
        {...props}
        dropdownRender={(menu) => <DropdownComponentUsed activeColor={activeColor}>{menu}</DropdownComponentUsed>}
        suffixIcon={suffixIcon ? suffixIcon : <ArrowDownIcon />}
        dropdownStyle={{ borderRadius: 10 }}
      >
        {children
          ? children
          : (SelectAllOpt ? [SelectAllOpt, ...sortedOptions] : sortedOptions).map((option, idx) => (
              <AntSelect.Option
                key={option.id || idx}
                value={option.id || ('value' in option && option.value) || idx}
                disabled={'disabled' in option && option.disabled}
                style={(props.fontOption && { fontFamily: 'value' in option && option.value }) || undefined}
                onMouseEnter={addScrollAnimation}
              >
                {option.label || ('name' in option && option.name) || option.id}
              </AntSelect.Option>
            ))}
      </SelectWrapper>
    </SelectContainer>
  );
};

export const Option = AntSelect.Option;

export default Select;
